// src/pages/SkuManagePage.tsx
import React, { useEffect, useMemo, useState } from "react";
import useSWR from "swr";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Pagination from "@mui/material/Pagination";

import {
  useSkuName,
  useSkuType,
  useSkuPage,
  useSkuSize,
  useSkuOrder,
  useSetSkuQuery,
  useResetSkuQuery,
} from "src/store/sku/sku.store";

import { getSkuListApi, createSkuApi, updateSkuApi } from "src/api/sku/skuApi";

import SkuFilterBar from "src/components/sku/SkuFilterBar";
import SkuListTable from "src/components/sku/SkuListTable";
import SkuEditModal, { SkuUpdatePayload } from "src/components/sku/SkuEditModal";

import type { SkuCreatePayload, SkuItem } from "src/types/sku";
import { snackbarStore } from "src/store/snackbarStore";

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

export default function SkuManagePage() {
  const skuName = useSkuName();
  const skuType = useSkuType();
  const page = useSkuPage();
  const size = useSkuSize();
  const order = useSkuOrder();

  const setQuery = useSetSkuQuery();
  const resetQuery = useResetSkuQuery();

  const [inputSkuName, setInputSkuName] = useState(skuName);
  useEffect(() => setInputSkuName(skuName), [skuName]);

  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState<"create" | "edit">("create");
  const [selected, setSelected] = useState<SkuItem | null>(null);

  const swrKey = useMemo(
    () => ["sku-list", skuName, skuType, page, size, order] as const,
    [skuName, skuType, page, size, order]
  );

  const { data, error, isValidating, mutate } = useSWR(
    swrKey,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getSkuListApi({
        skuName: skuName.trim() || undefined,
        skuType,
        isActive: true,
        deleted: false,
        page: apiPage,
        size,
        order,
      });
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const statusCode = (data as any)?.statusCode ?? (data as any)?.status_code;
  const ok = statusCode === undefined || statusCode === 0 || statusCode === 1;

  const apiErrorMsg = !ok ? (data as any)?.message || "SKU 목록 로드 실패" : null;

  const list = (data as any)?.data?.content ?? [];
  const totalPages = Math.max(1, (data as any)?.data?.totalPages ?? 1);
  const totalElements = (data as any)?.data?.totalElements ?? 0;

  const executeSearch = () => setQuery({ skuName: inputSkuName.trim(), page: 1 });

  const handleReset = () => {
    resetQuery();
    setInputSkuName("");
  };

  const openCreate = () => {
    setEditMode("create");
    setSelected(null);
    setEditOpen(true);
  };

  const openEdit = (item: SkuItem) => {
    setEditMode("edit");
    setSelected(item);
    setEditOpen(true);
  };

  // ✅ create / edit 타입 분기
  const onSubmitCreate = async (payload: SkuCreatePayload): Promise<boolean> => {
    try {
      const res = await createSkuApi(payload);
      const code = (res as any)?.statusCode ?? (res as any)?.status_code;
      const okCreate = code === 0 || code === 1;
      if (!okCreate) {
        toast((res as any)?.message || "SKU 생성 실패", "error");
        return false;
      }
      toast("SKU가 등록되었습니다.", "success");
      await mutate();
      return true;
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
      return false;
    }
  };

  const onSubmitEdit = async (payload: SkuUpdatePayload): Promise<boolean> => {
    try {
      // ✅ 서버 스펙: body에 id 포함
      const res = await updateSkuApi(payload);
      const code = (res as any)?.statusCode ?? (res as any)?.status_code;
      const okUpdate = code === 0 || code === 1;
      if (!okUpdate) {
        toast((res as any)?.message || "SKU 수정 실패", "error");
        return false;
      }
      toast("SKU가 수정되었습니다.", "success");
      await mutate();
      return true;
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
      return false;
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        minWidth: 800,
        overflowX: "auto",
      }}
    >
      <Paper
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          width: "100%",
          minWidth: 800,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          SKU 관리
        </Typography>

        <Divider sx={{ my: 2 }} />

        <SkuFilterBar
          skuName={inputSkuName}
          onChangeSkuName={setInputSkuName}
          skuType={skuType}
          size={size}
          order={order}
          onChangeImmediate={(patch) => setQuery({ ...patch, page: 1 })}
          onSearch={executeSearch}
          onReset={handleReset}
          onCreate={openCreate}
        />

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: "text.secondary" }}>
            총 {totalElements}건
          </Typography>

          {isValidating ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>불러오는 중...</Typography>
            </Stack>
          ) : null}
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {(error as any)?.message ?? "SKU 목록 로드 실패"}
          </Alert>
        ) : null}

        {apiErrorMsg ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {apiErrorMsg}
          </Alert>
        ) : null}
        <Box sx={{ minWidth: 900, maxWidth: 1200 }}>
          <SkuListTable list={list} onEdit={openEdit} />
        </Box>
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Pagination
            count={totalPages}
            page={Math.min(page, totalPages)}
            onChange={(_, v) => setQuery({ page: v })}
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Stack>
      </Paper>

      {/* ✅ 모달: mode에 따라 onSubmit을 다르게 연결 */}
      {editMode === "create" ? (
        <SkuEditModal
          open={editOpen}
          mode="create"
          onClose={() => {
            setEditOpen(false);
            setSelected(null);
          }}
          onSubmit={onSubmitCreate}
        />
      ) : selected ? (
        <SkuEditModal
          open={editOpen}
          mode="edit"
          item={selected}
          onClose={() => {
            setEditOpen(false);
            setSelected(null);
          }}
          onSubmit={onSubmitEdit}
        />
      ) : null}
    </Box>
  );
}
